import type { Meeting } from '@nexo/types';
import { useToast } from '@nexo/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useTeam } from '../../layouts/TeamContext';
import { api } from '../../lib/api';
import { cache } from '../../lib/cache';
import { keys } from '../../lib/queries';
import { meetingState } from './meetingUtils';
import { errorMessage } from '../../lib/forms';

/**
 * Teams-style "anyone with the link can join": reuses the live meeting of the conversation if it
 * already has a guest link, otherwise starts a quick meeting now (valid for about two hours).
 */
export function useInviteLink() {
  const { team } = useTeam();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();

  return async (channelId: string, title = t('Reunión rápida')): Promise<Meeting | null> => {
    try {
      const meetings =
        queryClient.getQueryData<Meeting[]>(keys.meetings(team.id)) ??
        (await api.meetings.list(team.id));
      let meeting: Meeting | null =
        meetings.find(
          (m) => m.channelId === channelId && m.guestUrl && meetingState(m) === 'live',
        ) ?? null;
      if (!meeting) {
        meeting = await api.meetings.create({
          title,
          channelId,
          startsAt: new Date().toISOString(),
          durationMinutes: 60,
          guestLink: true,
        });
        cache.upsertMeeting(queryClient, meeting);
      }
      if (meeting.guestUrl) {
        await navigator.clipboard.writeText(meeting.guestUrl).catch(() => undefined);
        toast({
          title: t('Enlace de invitación copiado'),
          description: t('Quien lo abra solo tiene que escribir su nombre para entrar.'),
          tone: 'success',
        });
      }
      return meeting;
    } catch (error) {
      toast({
        title: t('No se pudo crear el enlace'),
        description: errorMessage(error),
        tone: 'error',
      });
      return null;
    }
  };
}
